/**
 * MFA-capable auth store factory.
 *
 * Use this instead of `createAuthStore` for products that require TOTP/MFA
 * (currently: ENGAGE). The store is a superset of the base AuthStore —
 * it adds `mfaVerificationRequired`, `mfaEnrollmentRequired`, and their
 * setters, and changes `login` to return `MfaLoginResult` instead of `boolean`.
 *
 * @module @ayla/auth/mfa-store
 */

import { create, type StateCreator } from "zustand";
import { devtools } from "zustand/middleware";
import Cookies from "js-cookie";
import axios, { type AxiosInstance, type AxiosStatic } from "axios";
import { MFA_DETAIL_VERIFY, MFA_DETAIL_ENROLL } from "./types";
import type { AuthUser, MfaAuthStore } from "./types";

export interface MfaAuthStoreConfig {
  /** Login API endpoint — must accept x-www-form-urlencoded username/password */
  loginEndpoint: string;
  /** Endpoint used on every app mount to verify session and detect MFA state */
  checkAuthEndpoint: string;
  /** Axios instance to use (pass your app-local instance, not the shared singleton) */
  axios: AxiosInstance | AxiosStatic;
  /** Enable Redux DevTools integration. Should only be true in development. */
  devtools?: boolean;
}

const mfaInitialState = {
  isAuthenticated: false,
  isLoading: true,
  error: null,
  product: null,
  user: null,
  mfaVerificationRequired: false,
  mfaEnrollmentRequired: false,
};

export function createMfaAuthStore(config: MfaAuthStoreConfig) {
  const {
    loginEndpoint,
    checkAuthEndpoint,
    axios: ax,
    devtools: enableDevtools = false,
  } = config;

  const storeCreator: StateCreator<MfaAuthStore> = set => ({
    ...mfaInitialState,

    login: async (username, password) => {
      const response = await ax.post(
        loginEndpoint,
        new URLSearchParams({ username, password }),
        {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          withCredentials: true,
        }
      );

      const requiresMfa = response.data.requires_mfa ?? false;
      const requiresEnrollment = response.data.requires_mfa_enrollment ?? false;

      Cookies.set("user-email", username, {
        sameSite: "strict",
        // secure requires HTTPS — omit on http://localhost
        ...(typeof location !== "undefined" && location.protocol === "https:"
          ? { secure: true }
          : {}),
      });
      set({
        isAuthenticated: true,
        error: null,
        mfaVerificationRequired: requiresMfa,
        mfaEnrollmentRequired: requiresEnrollment,
      });

      return { requiresMfa, requiresEnrollment };
    },

    logout: () => {
      Cookies.remove("user-email");
      set({
        isAuthenticated: false,
        user: null,
        error: null,
        mfaVerificationRequired: false,
        mfaEnrollmentRequired: false,
      });
    },

    /**
     * Called on every app mount to restore auth state.
     * Parses MFA-specific 403 responses so a page refresh redirects to
     * /mfa/verify or /mfa/enroll rather than back to /login.
     */
    checkAuth: async () => {
      try {
        set({ isLoading: true });
        const response = await ax.get<AuthUser>(checkAuthEndpoint);
        set({
          isAuthenticated: true,
          user: response.data ?? null,
          error: null,
          isLoading: false,
          mfaVerificationRequired: false,
          mfaEnrollmentRequired: false,
        });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 403) {
          const detail: string = err.response?.data?.detail ?? "";
          if (detail === MFA_DETAIL_VERIFY) {
            set({
              isAuthenticated: true,
              mfaVerificationRequired: true,
              mfaEnrollmentRequired: false,
              isLoading: false,
              error: null,
            });
            return;
          }
          if (detail === MFA_DETAIL_ENROLL) {
            set({
              isAuthenticated: true,
              mfaEnrollmentRequired: true,
              mfaVerificationRequired: false,
              isLoading: false,
              error: null,
            });
            return;
          }
        }
        set({
          isAuthenticated: false,
          user: null,
          error: null,
          isLoading: false,
        });
      }
    },

    setMfaVerificationRequired: value =>
      set({ mfaVerificationRequired: value }),

    setMfaEnrollmentRequired: value => set({ mfaEnrollmentRequired: value }),

    clearMfaFlags: () =>
      set({ mfaVerificationRequired: false, mfaEnrollmentRequired: false }),

    setError: error => set({ error }),

    setProduct: product => set({ product }),

    reset: () => set(mfaInitialState),
  });

  return enableDevtools
    ? create<MfaAuthStore>()(
        devtools(storeCreator, { name: "engage-auth-store" })
      )
    : create<MfaAuthStore>()(storeCreator);
}
